import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import {
  Card,
  Button,
  Tag,
  Space,
  Input,
  Select,
  Tabs,
  Modal,
  Form,
  Tooltip,
  message,
  Popconfirm,
  Spin,
  Table,
  Alert,
} from "antd";
import {
  SearchOutlined,
  PlusOutlined,
  EditOutlined,
  ReloadOutlined,
  ExpandAltOutlined,
  CompressOutlined,
  DeleteOutlined,
  FolderOutlined,
  FileOutlined,
} from "@ant-design/icons";
import {
  getAccountTree,
  createAccount,
  updateAccount,
  deleteAccount,
  getAccountBalances,
  clearAccountsCache,
} from "../../api/accounting";
import { useSelector } from "react-redux";
import JournalEntries from "./JournalEntries";
import GeneralLedger from "./GeneralLedger";
import {
  getCachedData,
  setCachedData,
  invalidateCacheByPrefix,
  CacheTTL,
} from "../../context/AccountingDataCache";

const { Option } = Select;
const { TabPane } = Tabs;

const AdvanceAccounting = ({ selectedBrands: selectedBrandsProp = [] }) => {
  const profile = useSelector((state) => state.profile);

  // Use useMemo to prevent selectedBrands from changing on every render
  const selectedBrands = useMemo(() => {
    let brands =
      selectedBrandsProp && selectedBrandsProp.length > 0
        ? selectedBrandsProp
        : profile?.brands || [];

    console.log("AdvanceAccounting - Selected brands:", brands);
    return brands;
  }, [selectedBrandsProp, profile?.brands]);

  // Use the first selected brand for Chart of Accounts (can only show one brand at a time)
  const selectedBrand = selectedBrands.length > 0 ? selectedBrands[0] : null;

  // Create a stable key from selectedBrand to prevent unnecessary re-fetches
  const brandKey = useMemo(() => {
    return selectedBrand ? selectedBrand.id : "";
  }, [selectedBrand]);

  // Cache keys for global cache
  const accountTreeCacheKey = useMemo(() => {
    if (!brandKey) return null;
    return `account_tree_${brandKey}`;
  }, [brandKey]);

  const accountBalancesCacheKey = useMemo(() => {
    if (!brandKey) return null;
    return `account_balances_${brandKey}`;
  }, [brandKey]);

  // Check global cache for initial data - instant restore on navigation
  const initialAccountTree = useMemo(() => {
    if (!accountTreeCacheKey) return null;
    return getCachedData(accountTreeCacheKey);
  }, [accountTreeCacheKey]);

  const initialAccountBalances = useMemo(() => {
    if (!accountBalancesCacheKey) return null;
    return getCachedData(accountBalancesCacheKey);
  }, [accountBalancesCacheKey]);

  // Ref to track if we're currently fetching to prevent duplicate calls
  const fetchingRef = useRef(false);
  const lastFetchedBrandKey = useRef(initialAccountTree ? brandKey : "");

  // Chart of Accounts state - initialize from cache if available
  const [rawTree, setRawTree] = useState(() => initialAccountTree || []);
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [expandedKeys, setExpandedKeys] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [form] = Form.useForm();
  const [editing, setEditing] = useState(null);
  const [parentForNew, setParentForNew] = useState(null);
  const [accountBalances, setAccountBalances] = useState(() => initialAccountBalances || {});
  const [activeTab, setActiveTab] = useState(
    () =>
      localStorage.getItem("advance_accounting_active_tab") || "journal-entries"
  );

  // Sync active tab from localStorage on mount (in case navigated from external link)
  useEffect(() => {
    const savedTab = localStorage.getItem("advance_accounting_active_tab");
    if (savedTab && savedTab !== activeTab) {
      setActiveTab(savedTab);
    }
  }, []); // Only run on mount

  // Chart of Accounts functions
  const brandFilter = useMemo(
    () => (selectedBrand ? { brand: selectedBrand.id } : {}),
    [selectedBrand]
  );

  const fetchTree = useCallback(async (force = false) => {
    if (!selectedBrand || !accountTreeCacheKey) {
      setRawTree([]);
      return;
    }

    // Check global cache first if not forcing refresh
    if (!force) {
      const cachedData = getCachedData(accountTreeCacheKey);
      if (cachedData) {
        setRawTree(cachedData);
        return;
      }
    }

    setAccountsLoading(true);
    try {
      const response = await getAccountTree(brandFilter);

      // Handle different response formats
      let data;
      if (Array.isArray(response)) {
        data = response;
      } else if (response && Array.isArray(response.data)) {
        data = response.data;
      } else if (response && Array.isArray(response.results)) {
        data = response.results;
      } else {
        data = response || [];
      }

      setRawTree(data);
      
      // Cache in global cache - persists across page navigation
      setCachedData(accountTreeCacheKey, data, CacheTTL.MEDIUM);
    } catch (err) {
      console.error("Error fetching account tree:", err);
      message.error(err?.response?.data?.detail || "Failed to fetch accounts");
    } finally {
      setAccountsLoading(false);
    }
  }, [selectedBrand, brandFilter, accountTreeCacheKey]);

  // Fetch account balances
  const fetchAccountBalances = useCallback(async (force = false) => {
    if (!selectedBrand || !accountBalancesCacheKey) {
      setAccountBalances({});
      return;
    }

    // Check global cache first if not forcing refresh
    if (!force) {
      const cachedData = getCachedData(accountBalancesCacheKey);
      if (cachedData) {
        setAccountBalances(cachedData);
        return;
      }
    }

    try {
      const balances = await getAccountBalances({
        brand: selectedBrand.id,
      });
      setAccountBalances(balances || {});
      
      // Cache in global cache - persists across page navigation
      setCachedData(accountBalancesCacheKey, balances || {}, CacheTTL.MEDIUM);
    } catch (err) {
      console.error("Error fetching account balances:", err);
      setAccountBalances({});
    }
  }, [selectedBrand, accountBalancesCacheKey]);

  // Load tree data when brand changes - with guards to prevent duplicate calls
  useEffect(() => {
    // Skip if no brand selected
    if (!brandKey || brandKey === "") {
      setRawTree([]);
      lastFetchedBrandKey.current = "";
      return;
    }

    // Skip if we already have cached data for this brand
    if (rawTree.length > 0 && lastFetchedBrandKey.current === brandKey) {
      return;
    }

    // Skip if we're already fetching
    if (fetchingRef.current) {
      return;
    }

    // Set flags immediately (synchronously) before any async operations
    fetchingRef.current = true;
    lastFetchedBrandKey.current = brandKey;

    let cancelled = false;

    const treePromise = fetchTree();
    if (treePromise && typeof treePromise.then === "function") {
      treePromise
        .catch((err) => {
          if (!cancelled) {
            console.error("Error fetching account tree:", err);
          }
        })
        .finally(() => {
          if (!cancelled) {
            fetchingRef.current = false;
          }
        });
    } else {
      fetchingRef.current = false;
    }

    // Cleanup function to prevent state updates if component unmounts
    return () => {
      cancelled = true;
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandKey, rawTree.length]);

  // Fetch balances when brand changes
  useEffect(() => {
    if (!selectedBrand) return;
    
    // Skip if we already have cached balances
    if (Object.keys(accountBalances).length > 0 && accountBalancesCacheKey) {
      const cachedData = getCachedData(accountBalancesCacheKey);
      if (cachedData) return;
    }
    
    fetchAccountBalances();
  }, [selectedBrand, fetchAccountBalances, accountBalances, accountBalancesCacheKey]);

  const expandAll = () => {
    const keys = [];
    const walk = (nodes) =>
      nodes.forEach((n) => {
        keys.push(n.id);
        walk(n.children || []);
      });
    walk(filteredRawTree);
    setExpandedKeys(keys);
  };
  const collapseAll = () => setExpandedKeys([]);

  const openCreateTopLevel = () => {
    setEditing(null);
    setParentForNew(null);
    form.resetFields();
    setIsModalOpen(true);
  };

  const onAddChild = (node, depth) => {
    setEditing(null);
    setParentForNew(node);
    form.resetFields();
    form.setFieldsValue({ category: node.category });
    setIsModalOpen(true);
  };

  const onEditNode = (node, depth) => {
    setEditing(node);
    setParentForNew(null);
    form.resetFields();
    form.setFieldsValue({
      code: node.code || "",
      name: node.name || "",
      category: node.category || undefined,
      parent: node.parentId ?? null,
    });
    setIsModalOpen(true);
  };

  const onDeleteNode = async (node) => {
    try {
      await deleteAccount(node.id);
      message.success("Account deleted");
      
      clearAccountsCache();
      invalidateCacheByPrefix("account_tree_");
      invalidateCacheByPrefix("account_balances_");
      fetchTree(true);
      fetchAccountBalances(true);
      window.dispatchEvent(new CustomEvent("accountsUpdated"));
    } catch (err) {
      console.error(err);
      message.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      if (!selectedBrand) {
        message.error("Please select a brand first");
        return;
      }
      const payload = { ...values };
      if (parentForNew) payload.parent = parentForNew.id;
      // Ensure brand is included in payload
      payload.brand = selectedBrand.id;

      if (editing) {
        const res = await updateAccount(editing.id, payload);
        if (res?.id || res?.success !== false)
          message.success("Account updated");
      } else {
        const res = await createAccount(payload);
        if (res?.id || res?.success !== false)
          message.success("Account created");
      }
      setIsModalOpen(false);
      
      // Clear ALL cache layers (in-memory API cache + localStorage cache)
      clearAccountsCache();
      invalidateCacheByPrefix("account_tree_");
      invalidateCacheByPrefix("account_balances_");
      fetchTree(true);
      fetchAccountBalances(true);
      window.dispatchEvent(new CustomEvent("accountsUpdated"));
    } catch (err) {
      if (err?.errorFields) return;
      const data = err?.response?.data;
      if (data && typeof data === "object" && !Array.isArray(data)) {
        if (data.detail && typeof data.detail === "string") {
          message.error(data.detail);
          return;
        }
        if (
          Array.isArray(data.non_field_errors) &&
          data.non_field_errors.length
        ) {
          message.error(data.non_field_errors.join(", "));
          return;
        }
        const fields = Object.entries(data).map(([name, msgs]) => ({
          name,
          errors: Array.isArray(msgs) ? msgs : [String(msgs)],
        }));
        if (fields.length) {
          form.setFields(fields);
          return;
        }
      }
      console.error(err);
      const detail = err?.response?.data?.detail || err?.message;
      message.error(detail || "Save failed");
    }
  };

  const filterRawTree = useCallback(
    (nodes) => {
      if (!searchText) return nodes;
      const term = searchText.toLowerCase();
      const match = (n) =>
        `${n.code || ""} ${n.name || ""} ${n.category || ""}`
          .toLowerCase()
          .includes(term);
      const walk = (list) =>
        list
          .map((n) => ({ ...n, children: walk(n.children || []) }))
          .filter((n) => match(n) || (n.children && n.children.length));
      return walk(nodes);
    },
    [searchText]
  );

  const toTableData = useCallback(
    (nodes, parentName = null, depth = 1) => {
      // Calculate balances for each account and its children recursively
      const calculateAccountBalance = (account) => {
        let accountBalance = 0;

        // Get balance for this account - try both string and number keys
        const accountId = account.id;
        const balanceData =
          accountBalances[accountId] ||
          accountBalances[String(accountId)] ||
          accountBalances[Number(accountId)];

        // Debug: Log for ALL accounts to see what's happening
        if (Object.keys(accountBalances).length > 0) {
          console.log(
            `🔍 Account ${accountId} (${account.code} - ${account.name}):`,
            {
              accountId,
              accountIdType: typeof accountId,
              balanceData,
              hasChildren: !!(account.children && account.children.length > 0),
              isPostable: account.is_postable,
              found: !!balanceData,
              availableKeys: Object.keys(accountBalances).slice(0, 10),
            }
          );
        }

        // Calculate children balances recursively first
        let childrenBalance = 0;
        if (account.children && account.children.length > 0) {
          account.children = account.children.map((child) => {
            const childWithBalance = calculateAccountBalance(child);
            childrenBalance += childWithBalance.accountBalance;
            return childWithBalance;
          });
        }

        // Determine the account balance:
        // For parent accounts (with children): ALWAYS use sum of children balances
        // For leaf accounts (no children): use direct balance if available, otherwise 0
        if (account.children && account.children.length > 0) {
          // Parent account: always use sum of children balances
          // (children already have their direct balances calculated recursively)
          accountBalance = childrenBalance;
        } else if (balanceData) {
          // Leaf account with journal entries: use direct balance
          accountBalance = balanceData.balance || 0;
          if ([13, 19, 21, 32, 33, 36].includes(accountId)) {
            console.log(
              `✅ Account ${accountId} balance set to:`,
              accountBalance
            );
          }
        } else {
          // Leaf account without balance data = 0
          accountBalance = 0;
        }

        return {
          ...account,
          accountBalance: accountBalance,
        };
      };

      return (nodes || []).map((n) => {
        const accountWithBalance = calculateAccountBalance(n);
        return {
          key: n.id,
          id: n.id,
          name: n.name,
          code: n.code,
          category: n.category,
          is_postable: n.is_postable,
          parentName,
          parentId: depth === 1 ? null : undefined,
          depth,
          accountBalance: accountWithBalance.accountBalance,
          children: toTableData(n.children || [], n.name, depth + 1).map(c => ({ ...c, parentId: n.id })),
        };
      });
    },
    [accountBalances]
  );

  const flattenForParentSelect = useCallback(
    (nodes, excludeId, depth = 1) => {
      const options = [];
      (nodes || []).forEach((n) => {
        if (n.id === excludeId) return;
        if (!n.is_postable) {
          options.push({
            value: n.id,
            label: `${n.code || ""} ${n.name}`,
            depth,
            category: n.category,
          });
        }
        if (n.children?.length) {
          options.push(...flattenForParentSelect(n.children, excludeId, depth + 1));
        }
      });
      return options;
    },
    []
  );

  const filteredRawTree = useMemo(
    () => filterRawTree(rawTree),
    [rawTree, filterRawTree]
  );
  // Use table data directly without running balance calculation
  const tableData = useMemo(() => {
    return toTableData(filteredRawTree);
  }, [filteredRawTree, toTableData]);

  const getStatusTag = (status) => {
    const statusConfig = {
      pending: { color: "orange", text: "Pending" },
      posted: { color: "green", text: "Posted" },
      completed: { color: "blue", text: "Completed" },
    };

    const config = statusConfig[status] || { color: "default", text: status };
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  // Default column definition for AG Grid
  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: true,
      resizable: true,
      minWidth: 100,
    }),
    []
  );

  // Chart of Accounts columns
  const chartOfAccountsColumns = useMemo(
    () => [
      {
        title: "Account Name",
        dataIndex: "name",
        key: "name",
        width: 250,
        sorter: (a, b) => (a.name || "").localeCompare(b.name || ""),
        render: (text, record) => (
          <Space size={6}>
            {record.is_postable ? <FileOutlined /> : <FolderOutlined />}
            <span>{text}</span>
          </Space>
        ),
      },
      {
        title: "Account Code",
        dataIndex: "code",
        key: "code",
        width: 150,
        sorter: (a, b) => (a.code || "").localeCompare(b.code || ""),
      },
      {
        title: "Account Type",
        dataIndex: "category",
        key: "category",
        width: 150,
        sorter: (a, b) => (a.category || "").localeCompare(b.category || ""),
        render: (c) => <span style={{ textTransform: "capitalize" }}>{c}</span>,
      },
      {
        title: "Account Balance",
        dataIndex: "accountBalance",
        key: "accountBalance",
        width: 180,
        align: "right",
        sorter: (a, b) => (a.accountBalance || 0) - (b.accountBalance || 0),
        render: (balance) => {
          const value = balance || 0;
          return new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: "USD",
          }).format(value);
        },
      },
      {
        title: "Documents",
        dataIndex: "documents",
        key: "documents",
        width: 120,
        align: "center",
        render: () => "—",
      },
      {
        title: "Parent Account Name",
        dataIndex: "parentName",
        key: "parentName",
        width: 200,
        sorter: (a, b) =>
          (a.parentName || "").localeCompare(b.parentName || ""),
      },
      {
        title: "",
        key: "actions",
        width: 160,
        fixed: "right",
        render: (_, record) => (
          <Space size={4}>
            <Tooltip
              title={
                record.depth >= 3
                  ? "Max depth reached"
                  : record.is_postable
                  ? "Cannot add child to postable account"
                  : "Add child"
              }
            >
              <Button
                size="small"
                icon={<PlusOutlined />}
                onClick={() =>
                  onAddChild(
                    { ...record, id: record.id, category: record.category },
                    record.depth
                  )
                }
                disabled={record.depth >= 3 || record.is_postable}
              />
            </Tooltip>
            <Tooltip title="Edit">
              <Button
                size="small"
                icon={<EditOutlined />}
                onClick={() =>
                  onEditNode({ ...record, id: record.id }, record.depth)
                }
              />
            </Tooltip>
            <Popconfirm
              title="Delete this account?"
              okText="Yes"
              cancelText="No"
              onConfirm={() => onDeleteNode({ id: record.id })}
            >
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          </Space>
        ),
      },
    ],
    [onAddChild, onEditNode, onDeleteNode]
  );

  return (
    <div
      style={{ padding: "0", margin: "0", width: "100%", minHeight: "100vh" }}
    >
      <div style={{ padding: "16px", margin: "0" }}>
        <div style={{ marginBottom: "16px" }}>
          <h2
            style={{
              margin: "0 0 16px 0",
              fontSize: "24px",
              fontWeight: "600",
            }}
          >
            Advance Accounting
          </h2>
          {!selectedBrand && (
            <Alert
              message="Brand Selection Required"
              description="Please select a brand from the main accounting page to view journal entries and chart of accounts."
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}
        </div>

        <Tabs
          activeKey={activeTab}
          onChange={(key) => {
            setActiveTab(key);
            localStorage.setItem("advance_accounting_active_tab", key);
          }}
        >
          <TabPane tab="Journal Entries" key="journal-entries">
            <JournalEntries
              selectedBrand={selectedBrand}
              selectedBrands={selectedBrands}
            />
          </TabPane>

          <TabPane tab="Chart of Accounts" key="chart-of-accounts">
            <Card
              title="Active Accounts"
              extra={
                <Space>
                  <Input
                    allowClear
                    placeholder="Search accounts..."
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    prefix={<SearchOutlined />}
                    style={{ width: 240 }}
                  />
                  <Tooltip title="Collapse all">
                    <Button icon={<CompressOutlined />} onClick={collapseAll} />
                  </Tooltip>
                  <Tooltip title="Expand all">
                    <Button icon={<ExpandAltOutlined />} onClick={expandAll} />
                  </Tooltip>
                  <Tooltip title="Refresh">
                    <Button
                      icon={<ReloadOutlined />}
                      onClick={fetchTree}
                      loading={accountsLoading}
                    />
                  </Tooltip>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={openCreateTopLevel}
                  >
                    New
                  </Button>
                </Space>
              }
            >
              <Spin spinning={accountsLoading} tip="Loading accounts...">
                {tableData.length === 0 && !accountsLoading ? (
                  <div
                    style={{
                      textAlign: "center",
                      padding: "40px",
                      color: "#999",
                    }}
                  >
                    No accounts found. Raw tree count: {rawTree.length}
                  </div>
                ) : (
                  <Table
                    size="middle"
                    rowKey="id"
                    columns={chartOfAccountsColumns}
                    dataSource={tableData}
                    pagination={false}
                    scroll={{ x: 1400 }}
                    expandable={{
                      expandedRowKeys: expandedKeys,
                      onExpandedRowsChange: setExpandedKeys,
                    }}
                  />
                )}
              </Spin>
            </Card>
          </TabPane>

          <TabPane tab="General Ledger" key="general-ledger">
            <GeneralLedger
              selectedBrand={selectedBrand}
              selectedBrands={selectedBrands}
            />
          </TabPane>
        </Tabs>
      </div>

      {/* Chart of Accounts Modal */}
      <Modal
        title={
          editing
            ? "Edit Account"
            : parentForNew
            ? "Add Child Account"
            : "Add Top-Level Account"
        }
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        onOk={handleOk}
        okText={editing ? "Save" : "Create"}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          {(() => {
            const ROOT_CATEGORIES = [
              "Asset",
              "Liability",
              "Equity",
              "Revenue",
              "Expense",
            ];
            const rootOptions = ROOT_CATEGORIES;
            return (
              <>
                <Form.Item
                  name="code"
                  label="Code"
                  rules={[
                    { required: true, message: "Please enter an account code" },
                  ]}
                >
                  <Input placeholder="e.g., 1-1-001" />
                </Form.Item>
                <Form.Item
                  name="name"
                  label="Name"
                  rules={[{ required: true, message: "Please enter a name" }]}
                >
                  <Input />
                </Form.Item>
                <Form.Item
                  label="Category"
                  name="category"
                  rules={[
                    {
                      required: !parentForNew,
                      message: "Please select category",
                    },
                  ]}
                >
                  <Select
                    disabled={!!parentForNew || !!editing}
                    placeholder={
                      parentForNew
                        ? `Inherited: ${parentForNew.category}`
                        : "Select main category"
                    }
                  >
                    {(parentForNew ? [parentForNew.category] : rootOptions).map(
                      (c) => (
                        <Select.Option key={c} value={c}>
                          {c.charAt(0).toUpperCase() + c.slice(1)}
                        </Select.Option>
                      )
                    )}
                  </Select>
                </Form.Item>
                {editing && (
                  <Form.Item name="parent" label="Parent Account">
                    <Select
                      placeholder="Top Level (no parent)"
                      allowClear
                      showSearch
                      optionFilterProp="label"
                    >
                      {flattenForParentSelect(filteredRawTree, editing?.id)
                        .filter(
                          (opt) =>
                            !editing?.category ||
                            opt.category === editing.category
                        )
                        .map((opt) => (
                          <Select.Option
                            key={opt.value}
                            value={opt.value}
                            label={opt.label}
                          >
                            {"—".repeat(Math.max(0, opt.depth - 1))} {opt.label}
                          </Select.Option>
                        ))}
                    </Select>
                  </Form.Item>
                )}
                {editing &&
                Array.isArray(editing.children) &&
                editing.children.length > 0 ? (
                  <Tag color="gold">
                    This account has children. Changing structure is restricted.
                  </Tag>
                ) : null}
              </>
            );
          })()}
        </Form>
      </Modal>
    </div>
  );
};

export default AdvanceAccounting;
